/**
 * DevTools Restriction & Advanced Anti-Tamper Security Engine
 * 
 * Target Rules:
 * - Active on login page for everyone (when unauthenticated / no active role)
 * - Active for logged-in users ONLY when RoleID === 7
 * - Inactive (disabled) for all other roles (RoleID !== 7)
 * 
 * Enhanced Defenses:
 * - Network API Firewall: Intercepts & aborts fetch / XMLHttpRequest if DevTools is active
 * - Navigation & Tab Re-entry Guard: Instantly purges session storage if user returns with DevTools open
 * - Real-time docked & console getter detection with auto-recovery
 */

let originalFetch = null;
let originalXhrSend = null;
let isNetworkBlocked = false;
let securityInterval = null;
let consecutiveHits = 0;
const HITS_REQUIRED = 2;
const POLLING_INTERVAL_MS = 800;

/**
 * Checks if DevTools security should run in the current environment.
 * - REACT_APP_DEVTOOLS_SECURITY=true/false in .env forces it on/off
 * - Otherwise disabled for local development (npm start or localhost), enabled elsewhere
 */
export function isDevToolsSecurityEnabled() {
  const override = process.env.REACT_APP_DEVTOOLS_SECURITY;
  if (override === "true") return true;
  if (override === "false") return false;

  if (process.env.NODE_ENV === "development") return false;

  const host = window.location.hostname;
  const isLocalHost = host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  return !isLocalHost;
}

/**
 * Checks if current page / user role is subject to DevTools restriction.
 * Restricted strictly ONLY for RoleID === 7.
 */
export function isDevToolsRestricted() {
  try {
    const roleIdRaw = sessionStorage.getItem("RoleIDSS");
    let roleId = roleIdRaw;
    if (roleId === null || roleId === undefined || roleId === "") {
      const loginData = sessionStorage.getItem("loginDataSS");
      if (loginData) {
        try {
          const parsed = JSON.parse(loginData);
          roleId = parsed?.RoleID;
        } catch (e) {
          // JSON parse error fallback
        }
      }
    }

    // Restricted strictly ONLY for RoleID === 7
    if (roleId !== null && roleId !== undefined && String(roleId).trim() !== "") {
      return String(roleId) === "7" || Number(roleId) === 7;
    }

    // Disabled for all other roles, unauthenticated users, and login pages
    return false;
  } catch (err) {
    return false;
  }
}

/**
 * Returns the warning overlay DOM element.
 */
function getOverlayElement() {
  return document.getElementById("devtools-warning");
}

/**
 * Controls visibility of the warning overlay (Commented out for all logins and all pages).
 */
function setOverlayVisible(visible) {
  /*
  const overlay = getOverlayElement();
  if (overlay) {
    overlay.style.display = visible ? "flex" : "none";
  }
  */
}

/**
 * Checks if DevTools is open via window dimensions or console getter.
 */
export function checkDevToolsOpen() {
  // Method 1: Window Dimension Differential (Docked DevTools)
  const widthDiff = window.outerWidth - window.innerWidth;
  const heightDiff = window.outerHeight - window.innerHeight;
  const isDocked = widthDiff > 160 || heightDiff > 160;

  if (isDocked) {
    return true;
  }

  // Method 2: Console Property Getter Heuristic (Console open)
  let getterTriggered = false;
  const element = new Image();
  Object.defineProperty(element, "id", {
    get: function () {
      getterTriggered = true;
    },
    configurable: true
  });

  console.log(element);
  console.clear();

  if (getterTriggered) {
    return true;
  }

  return false;
}

let originalXhrOpen = null;

/**
 * Network Firewall: Intercepts fetch and XMLHttpRequest to prevent network API exposure.
 */
function installNetworkFirewall() {
  if (typeof window === "undefined") return;

  // Intercept window.fetch
  if (!originalFetch && window.fetch) {
    originalFetch = window.fetch;
    window.fetch = function (...args) {
      if (isNetworkBlocked && isDevToolsRestricted()) {
        const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
        if (url.includes('FillCombo') || url.includes('fillList') || window.location.hash.includes('/login')) {
          return originalFetch.apply(this, args);
        }
        console.warn("[Security] Network request blocked by DevTools restriction policy.");
        return Promise.reject(new Error("Network request blocked due to security policy."));
      }
      return originalFetch.apply(this, args);
    };
  }

  // Intercept XMLHttpRequest open to record requested URL
  if (!originalXhrOpen && window.XMLHttpRequest && window.XMLHttpRequest.prototype.open) {
    originalXhrOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._requestUrl = url;
      return originalXhrOpen.call(this, method, url, ...rest);
    };
  }

  // Intercept XMLHttpRequest send
  if (!originalXhrSend && window.XMLHttpRequest && window.XMLHttpRequest.prototype.send) {
    originalXhrSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.send = function (...args) {
      if (isNetworkBlocked && isDevToolsRestricted()) {
        const url = this._requestUrl || '';
        if (url.includes('FillCombo') || url.includes('fillList') || window.location.hash.includes('/login')) {
          return originalXhrSend.apply(this, args);
        }
        console.warn("[Security] XHR request blocked by DevTools restriction policy.");
        try {
          this.abort();
        } catch (e) { }
        return;
      }
      return originalXhrSend.apply(this, args);
    };
  }
}

/**
 * Purges session and local storage to prevent data exfiltration when returning with DevTools.
 */
function purgeSensitiveSession() {
  try {
    sessionStorage.clear();
    localStorage.clear();
  } catch (e) { }
}

/**
 * Redirects the user to the login screen upon security violation.
 */
function redirectToLogin() {
  try {
    if (window.location.hash !== "#/login/ITME2026" && window.location.hash !== "#/") {
      window.location.hash = "#/login/ITME2026";
    }
  } catch (e) { }
}

/**
 * Instant Re-entry / Navigation Guard
 * Executed on pageshow (bfcache), visibilitychange, focus, and history navigation.
 */
function handleInstantReentryGuard() {
  if (!isDevToolsRestricted()) {
    isNetworkBlocked = false;
    consecutiveHits = 0;
    // setOverlayVisible(false);
    return;
  }

  const isDevToolsOpen = checkDevToolsOpen();
  if (isDevToolsOpen) {
    // 1. Block network APIs immediately so nothing shows in Network tab
    isNetworkBlocked = true;
    consecutiveHits = HITS_REQUIRED;

    // 2. Immediately purge session & local storage
    purgeSensitiveSession();

    // 3. Navigate directly to login screen
    redirectToLogin();

    // 4. Show the warning overlay to close DevTools (Commented out)
    // setOverlayVisible(true);
  } else {
    isNetworkBlocked = false;
    consecutiveHits = 0;
    // setOverlayVisible(false);
  }
}

/**
 * Periodic Security Polling Loop
 */
function handleSecurityScan() {
  if (!isDevToolsRestricted()) {
    isNetworkBlocked = false;
    consecutiveHits = 0;
    // setOverlayVisible(false);
    return;
  }

  const isDevToolsOpen = checkDevToolsOpen();

  if (isDevToolsOpen) {
    consecutiveHits++;
    if (consecutiveHits >= HITS_REQUIRED) {
      // 1. Block network APIs
      isNetworkBlocked = true;

      // 2. Immediately purge session & local storage
      purgeSensitiveSession();

      // 3. Navigate directly to login screen
      redirectToLogin();

      // 4. Show the warning overlay to close DevTools (Commented out)
      // setOverlayVisible(true);
    }
  } else {
    isNetworkBlocked = false;
    consecutiveHits = 0;
    // setOverlayVisible(false);
  }
}

/**
 * Keyboard Shortcut Restrictions
 */
function handleKeyDown(e) {
  if (!isDevToolsRestricted()) {
    return;
  }

  const keyCode = e.keyCode || e.which;
  const isCtrl = e.ctrlKey || e.metaKey;
  const isShift = e.shiftKey;

  // F12
  if (keyCode === 123 || e.key === "F12") {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Ctrl + Shift + I (Inspect Elements)
  if (isCtrl && isShift && (keyCode === 73 || e.key === "I" || e.key === "i")) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Ctrl + Shift + J (Console)
  if (isCtrl && isShift && (keyCode === 74 || e.key === "J" || e.key === "j")) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Ctrl + Shift + C (Element selector)
  if (isCtrl && isShift && (keyCode === 67 || e.key === "C" || e.key === "c")) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Ctrl + U (View Source)
  if (isCtrl && (keyCode === 85 || e.key === "U" || e.key === "u")) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Ctrl + S (Save Page)
  if (isCtrl && (keyCode === 83 || e.key === "S" || e.key === "s")) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
}

/**
 * Context Menu (Right-Click) Restriction
 */
function handleContextMenu(e) {
  if (!isDevToolsRestricted()) {
    return;
  }
  e.preventDefault();
  return false;
}

/**
 * Visibility change handler (tab switching)
 */
function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    handleInstantReentryGuard();
  }
}

/**
 * Page Show handler (back/forward bfcache navigation)
 */
function handlePageShow(event) {
  handleInstantReentryGuard();
}

/**
 * Window Focus handler
 */
function handleWindowFocus() {
  handleInstantReentryGuard();
}

/**
 * Initializes listeners, network firewall, navigation guards, and polling interval.
 * Returns cleanup function.
 */
export function initDevToolsSecurity() {
  // Skip entirely in local / disabled environments
  if (!isDevToolsSecurityEnabled()) {
    // setOverlayVisible(false);
    return () => { };
  }

  // Install Network Firewall
  installNetworkFirewall();

  // Remove existing listeners before binding
  window.removeEventListener("keydown", handleKeyDown, true);
  document.removeEventListener("contextmenu", handleContextMenu, true);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pageshow", handlePageShow);
  window.removeEventListener("focus", handleWindowFocus);
  window.removeEventListener("popstate", handleInstantReentryGuard);
  window.removeEventListener("hashchange", handleInstantReentryGuard);

  if (securityInterval) {
    clearInterval(securityInterval);
    securityInterval = null;
  }

  // Bind security event listeners
  window.addEventListener("keydown", handleKeyDown, true);
  document.addEventListener("contextmenu", handleContextMenu, true);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("focus", handleWindowFocus);
  window.addEventListener("popstate", handleInstantReentryGuard);
  window.addEventListener("hashchange", handleInstantReentryGuard);

  // Initial immediate check upon mount
  handleInstantReentryGuard();

  // Polling loop
  securityInterval = setInterval(handleSecurityScan, POLLING_INTERVAL_MS);

  return () => {
    window.removeEventListener("keydown", handleKeyDown, true);
    document.removeEventListener("contextmenu", handleContextMenu, true);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pageshow", handlePageShow);
    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("popstate", handleInstantReentryGuard);
    window.removeEventListener("hashchange", handleInstantReentryGuard);

    if (securityInterval) {
      clearInterval(securityInterval);
      securityInterval = null;
    }
  };
}
